import { useMemo } from "react";
import { TargetSchema } from "@/types";
import type { SchemaFieldDefinition } from "@/constants/schema-fields/schemaFields";
import { SCHEMA_LABELS } from "../../utils/reviewUtils";
import { CRITICAL_FIELDS } from "../document-review/criticalFields";
import { targetKey, type CriticalCue, type UnifiedTarget } from "./TargetFieldSelect";

/**
 * Resolves a (schema, field) mapping to its registry dropdown option, or a
 * synthetic "unknown" target when the field doesn't resolve for its schema
 * (#6425: a legacy/drifted mapping to a nonexistent field). Single source of
 * truth for the unknown-target rule so the grid and PDF surfaces can't drift
 * apart — both build current-target chips through here.
 */
export const resolveTarget = (schema: string, field: string, optionByKey: Map<string, UnifiedTarget>): UnifiedTarget =>
  optionByKey.get(`${schema}:${field}`) ?? {
    schema,
    field,
    // Raw field name (not a friendly title-case) + isUnknown so it renders as an
    // error chip rather than a valid-looking one.
    label: field,
    group: SCHEMA_LABELS[schema as TargetSchema] ?? schema,
    isUnknown: true,
  };

/**
 * The current target chips for one source header/label, across all schemas,
 * each resolved through {@link resolveTarget}. Shared by the grid and PDF
 * surfaces so the target-building rule lives once.
 *
 * Unknown targets are floated to the front: TargetFieldSelect renders with
 * `limitTags={1}` and MUI truncates *after* `renderTags` when the control is
 * unfocused (the default review state), so a valid chip ahead of an unknown one
 * would hide the error affordance for that column (#6425). Ordering is otherwise
 * stable — stored-mapping order within the unknown and known groups.
 */
export const currentTargetsForHeader = (
  header: string,
  allSchemaKeys: string[],
  editedMappingsBySchema: Record<string, Record<string, string[]>>,
  optionByKey: Map<string, UnifiedTarget>
): UnifiedTarget[] => {
  const result: UnifiedTarget[] = [];
  for (const schema of allSchemaKeys) {
    for (const field of editedMappingsBySchema[schema]?.[header] ?? []) {
      result.push(resolveTarget(schema, field, optionByKey));
    }
  }
  return result.sort((a, b) => (a.isUnknown ? 0 : 1) - (b.isUnknown ? 0 : 1));
};

/**
 * Shared option model for the cross-schema mapping dropdown (#6118) — the union
 * of every schema's fields, a key→option index, and the required/recommended
 * cues from the critical-fields contract. Used by every remap surface
 * (spreadsheet grid, PDF map-on-document) so their dropdowns stay identical.
 */
export const useMappingOptions = (fieldsBySchema: Record<string, SchemaFieldDefinition[]>, allSchemaKeys: string[]) => {
  const options = useMemo<UnifiedTarget[]>(() => {
    const opts: UnifiedTarget[] = [];
    for (const schema of allSchemaKeys) {
      const group = SCHEMA_LABELS[schema as TargetSchema] ?? schema;
      for (const f of fieldsBySchema[schema] ?? []) {
        opts.push({ schema, field: f.field, label: f.label, group });
      }
    }
    // Sort by (group, label). This is load-bearing: suggestedFieldForSource
    // iterates this array with a strict `>` and returns the FIRST option to
    // reach the max score, so ties resolve to the alphabetically-first label.
    // The pre-refactor grid sorted here; dropping it silently changed which
    // field gets the "Suggested" cue for ambiguous headers.
    return opts.sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label));
  }, [allSchemaKeys, fieldsBySchema]);

  const optionByKey = useMemo(() => {
    const map = new Map<string, UnifiedTarget>();
    for (const o of options) map.set(targetKey(o), o);
    return map;
  }, [options]);

  const criticalByKey = useMemo(() => {
    const m = new Map<string, CriticalCue>();
    for (const schema of allSchemaKeys) {
      for (const e of CRITICAL_FIELDS[schema as TargetSchema] ?? []) {
        if (e.severity === "required" || e.severity === "recommended") {
          m.set(`${schema}:${e.field}`, { severity: e.severity, impact: e.impact });
        }
      }
    }
    return m;
  }, [allSchemaKeys]);

  return { options, optionByKey, criticalByKey };
};

/** Fields currently mapped by ANY source (schema:field) — disabled elsewhere so
 *  a field can't be mapped twice. */
export const useClaimedKeys = (
  editedMappingsBySchema: Record<string, Record<string, string[]>>,
  allSchemaKeys: string[]
) =>
  useMemo(() => {
    const s = new Set<string>();
    for (const schema of allSchemaKeys) {
      for (const fields of Object.values(editedMappingsBySchema[schema] ?? {})) {
        for (const field of fields) s.add(`${schema}:${field}`);
      }
    }
    return s;
  }, [editedMappingsBySchema, allSchemaKeys]);

/**
 * Best-guess target field for a source (header or PDF label), by normalized
 * name match — powers the "Suggested" cue. Returns `schema:field` or null.
 */
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
export const suggestedFieldForSource = (sourceLabel: string, options: UnifiedTarget[]): string | null => {
  const h = norm(sourceLabel);
  if (!h) return null;
  let best: string | null = null;
  let bestScore = 0;
  for (const o of options) {
    const l = norm(o.label);
    const f = norm(o.field);
    let score = 0;
    if (l === h || f === h) score = 3;
    else if (h.includes(l) || l.includes(h) || h.includes(f) || f.includes(h)) score = 2;
    if (score > bestScore) {
      bestScore = score;
      best = targetKey(o);
    }
  }
  return bestScore >= 2 ? best : null;
};
